import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Users, Calendar } from "lucide-react";
import { MAGIC_PHOTOS, CONTACT } from "../data/cruiseData";

const DURATION = 4500; // ms per slide

const SLIDES = [
 {
 id: "nye",
 photo: MAGIC_PHOTOS.fireworksCollage,
 tag: "NYE & Vivid",
 chipClass: "chip-nye",
 eyebrow: "New Year's Eve 2026",
 eyebrowClass: "purple-eyebrow",
 title: <>Celebrate on Sydney <em className="italic-gradient not-italic">Harbour in style.</em></>,
 tagline: "Birthdays, Weddings, Corporate Events & NYE Cruises.",
 body: "Private Sydney Harbour cruises for birthdays, weddings, corporate events, engagements, Christmas parties, New Year's Eve and unforgettable celebrations aboard the luxurious Magic catamaran.",
 pillEmoji: "✨",
 pillText: "NYE Fireworks Cruise",
 wash: "linear-gradient(135deg, hsla(280,80%,55%,0.55), hsla(330,90%,60%,0.35), hsla(45,100%,55%,0.45))",
 hashtags: "#SydneyHarbour #NYE2026 #Fireworks",
 },
 {
 id: "corporate",
 photo: MAGIC_PHOTOS.corporateLounge,
 tag: "Corporate",
 chipClass: "chip-corp",
 eyebrow: "Corporate Charters",
 eyebrowClass: "purple-eyebrow",
 title: <>Sydney Harbour <em className="italic-gradient not-italic">corporate cruises.</em></>,
 tagline: "EOFY, Christmas parties & client hospitality.",
 body: "Premium corporate events on Sydney Harbour, EOFY parties, Christmas functions, product launches, conference dinners and client hospitality cruises aboard the Magic.",
 pillEmoji: "🥂",
 pillText: "Corporate Cocktail Cruise",
 wash: "linear-gradient(135deg, hsla(195,90%,55%,0.55), hsla(215,85%,45%,0.45), hsla(220,60%,30%,0.45))",
 hashtags: "#CorporateEvents #SydneyHarbour",
 },
 {
 id: "birthday",
 photo: MAGIC_PHOTOS.hensBridal,
 tag: "Birthdays",
 chipClass: "chip-birthday",
 eyebrow: "Milestone Birthdays",
 eyebrowClass: "purple-eyebrow",
 title: <>Unforgettable Sydney <em className="italic-gradient not-italic">harbour cruises.</em></>,
 tagline: "Cake. Cocktails. Coathanger.",
 body: "21sts, 30ths, 40ths and milestone birthdays, the Magic turns your celebration into a Sydney Harbour legend with a DJ, full bar and 360° harbour views.",
 pillEmoji: "🎂",
 pillText: "Birthday Party Cruise",
 wash: "linear-gradient(135deg, hsla(335,90%,60%,0.55), hsla(15,95%,60%,0.45), hsla(45,100%,55%,0.4))",
 hashtags: "#BirthdayCruise #SydneyHarbour",
 },
 {
 id: "private",
 photo: MAGIC_PHOTOS.championToast,
 tag: "Weddings",
 chipClass: "chip-private",
 eyebrow: "Wedding Charters",
 eyebrowClass: "purple-eyebrow",
 title: <>Wedding cruises on <em className="italic-gradient not-italic">Sydney Harbour.</em></>,
 tagline: "Your harbour. Your night. Your guest list.",
 body: "Weddings, engagements, hens parties and exclusive private events, the entire Magic catamaran for up to 100 guests, fully customisable from menu to music.",
 pillEmoji: "⭐",
 pillText: "Wedding Cruises",
 wash: "linear-gradient(135deg, hsla(35,95%,60%,0.55), hsla(340,85%,65%,0.4), hsla(200,90%,55%,0.4))",
 hashtags: "#PrivateCharter #SydneyHarbour",
 }];

function NyeOverlay() {
 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 {/* Firework bursts */}
 {[
 { x: "18%", y: "22%", c: "#FFD700", cls: "burst-1" },
 { x: "78%", y: "18%", c: "#E879F9", cls: "burst-2" },
 { x: "45%", y: "12%", c: "#60A5FA", cls: "burst-3" },
 { x: "12%", y: "40%", c: "#FFD700", cls: "burst-4" },
 { x: "85%", y: "45%", c: "#FB7185", cls: "burst-5" }].map((b, i) => (
 <svg key={i} className={`absolute ${b.cls}`} style={{ left: b.x, top: b.y, width: 180, height: 180 }} viewBox="0 0 100 100">
 {Array.from({ length: 16 }).map((_, k) => {
 const a = (k * Math.PI) / 8;
 return <line key={k} x1="50" y1="50" x2={50 + Math.cos(a) * 38} y2={50 + Math.sin(a) * 38} stroke={b.c} strokeWidth="1.5" strokeLinecap="round" opacity="0.92" />;
 })}
 <circle cx="50" cy="50" r="3" fill="#fff" />
 </svg>
 ))}
 {/* Confetti */}
 {Array.from({ length: 26 }).map((_, i) => {
 const colors = ["#FFD700", "#A78BFA", "#F472B6", "#34D399", "#60A5FA"];
 const c = colors[i % colors.length];
 const left = (i * 4 + 5) % 95;
 const cx = (i % 2 === 0 ? 30 : -30) + i;
 return <div key={i} className="absolute confetti-piece" style={{ left: `${left}%`, top: "-20px", width: 8, height: 12, background: c, borderRadius: 2, "--cx": `${cx}px`, animationDelay: `${(i * 120) % 1500}ms` }} />;
 })}
 {/* 2026 sparkle */}
 <div className="absolute top-6 right-8 text-white font-display text-3xl font-extrabold" style={{ textShadow: "0 0 24px gold, 0 0 8px white" }}>2026 ✨</div>
 </div>
 );
}

function CorporateOverlay() {
 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 {/* Champagne flutes clinking */}
 <svg className="absolute left-[15%] top-[55%] w-28 h-28 twinkle twinkle-d1" viewBox="0 0 100 100">
 <path d="M30 18 L40 50 Q40 58 50 58 Q60 58 60 50 L70 18 Z" fill="rgba(255,255,255,0.85)" stroke="#FFD700" strokeWidth="1.5" />
 <rect x="48" y="58" width="4" height="24" fill="rgba(255,255,255,0.7)" />
 <ellipse cx="50" cy="84" rx="14" ry="3" fill="rgba(255,255,255,0.7)" />
 <circle cx="42" cy="32" r="2" fill="#FFD700" opacity="0.8" />
 <circle cx="55" cy="38" r="1.5" fill="#FFD700" opacity="0.7" />
 </svg>
 <svg className="absolute right-[20%] top-[55%] w-28 h-28 twinkle twinkle-d3" viewBox="0 0 100 100">
 <path d="M30 18 L40 50 Q40 58 50 58 Q60 58 60 50 L70 18 Z" fill="rgba(255,255,255,0.85)" stroke="#FFD700" strokeWidth="1.5" />
 <rect x="48" y="58" width="4" height="24" fill="rgba(255,255,255,0.7)" />
 <ellipse cx="50" cy="84" rx="14" ry="3" fill="rgba(255,255,255,0.7)" />
 </svg>
 {/* Rising bubbles */}
 {Array.from({ length: 18 }).map((_, i) => (
 <div key={i} className="absolute bubble rounded-full bg-white/70" style={{ left: `${(i * 6 + 12) % 88}%`, bottom: "-20px", width: 8 + (i % 4) * 3, height: 8 + (i % 4) * 3, animationDelay: `${i * 160}ms` }} />
 ))}
 {/* Sparkles */}
 {[{l:"22%",t:"30%",d:"twinkle-d1"},{l:"68%",t:"28%",d:"twinkle-d2"},{l:"45%",t:"22%",d:"twinkle-d4"},{l:"82%",t:"36%",d:"twinkle-d5"},{l:"14%",t:"48%",d:"twinkle-d6"}].map((s,i)=>(
 <div key={i} className={`absolute twinkle ${s.d} text-yellow-300 text-2xl`} style={{left:s.l, top:s.t, textShadow:"0 0 12px gold"}}>✦</div>
 ))}
 {/* CBD silhouette bottom */}
 <svg className="absolute bottom-0 left-0 right-0 w-full h-12 opacity-30" viewBox="0 0 800 60" preserveAspectRatio="none">
 <path d="M0 60 L0 30 L40 30 L40 18 L80 18 L80 35 L120 35 L120 10 L160 10 L160 28 L200 28 L200 5 L240 5 L240 22 L300 22 L300 14 L360 14 L360 32 L420 32 L420 8 L480 8 L480 24 L540 24 L540 16 L600 16 L600 30 L660 30 L660 12 L720 12 L720 26 L800 26 L800 60 Z" fill="rgba(15,30,60,0.7)" />
 </svg>
 </div>
 );
}

function BirthdayOverlay() {
 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 {/* Floating balloons */}
 {[
 { l: "10%", c: "#FB7185", d: 0 },{ l: "22%", c: "#FFD700", d: 400 },{ l: "75%", c: "#A78BFA", d: 200 },{ l: "85%", c: "#F472B6", d: 800 },{ l: "55%", c: "#60A5FA", d: 1200 },{ l: "40%", c: "#FB7185", d: 1600 }].map((b, i) => (
 <div key={i} className="absolute balloon" style={{ left: b.l, bottom: "-60px", animationDelay: `${b.d}ms` }}>
 <svg width="56" height="80" viewBox="0 0 56 80">
 <ellipse cx="28" cy="30" rx="22" ry="28" fill={b.c} opacity="0.95" />
 <ellipse cx="22" cy="22" rx="6" ry="9" fill="white" opacity="0.4" />
 <path d="M28 58 L28 80" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" />
 </svg>
 </div>
 ))}
 {/* Heavy confetti */}
 {Array.from({ length: 36 }).map((_, i) => {
 const colors = ["#FB7185", "#FFD700", "#A78BFA", "#F472B6", "#60A5FA", "#FCA5A5"];
 const c = colors[i % colors.length];
 return <div key={i} className="absolute confetti-piece" style={{ left: `${(i * 3 + 2) % 96}%`, top: "-20px", width: 7, height: 11, background: c, borderRadius: 1, "--cx": `${(i % 2 === 0 ? 20 : -20)}px`, animationDelay: `${(i * 100) % 2000}ms` }} />;
 })}
 {/* Candle sparkles */}
 {Array.from({ length: 8 }).map((_, i) => (
 <div key={i} className={`absolute twinkle twinkle-d${(i % 7) + 1} text-yellow-200`} style={{ left: `${15 + i * 9}%`, top: `${30 + (i % 3) * 8}%`, fontSize: "1.5rem", textShadow: "0 0 12px gold" }}>✨</div>
 ))}
 </div>
 );
}

function PrivateOverlay() {
 return (
 <div className="absolute inset-0 pointer-events-none overflow-hidden">
 {/* Warm bokeh */}
 {Array.from({ length: 16 }).map((_, i) => {
 const sz = 30 + (i % 4) * 18;
 return <div key={i} className="absolute bokeh rounded-full" style={{ left: `${(i * 7) % 95}%`, bottom: "-30px", width: sz, height: sz, background: i % 2 === 0 ? "radial-gradient(circle, hsla(35,100%,75%,0.8), hsla(35,100%,60%,0))" : "radial-gradient(circle, hsla(340,85%,75%,0.7), hsla(340,85%,55%,0))", "--bx": `${(i % 2 === 0 ? 18 : -18)}px`, animationDelay: `${i * 180}ms` }} />;
 })}
 {/* Floating hearts */}
 {[{ l: "18%", d: 0, c: "#FB7185" },{ l: "72%", d: 600, c: "#F472B6" },{ l: "45%", d: 1200, c: "#FCA5A5" },{ l: "88%", d: 1800, c: "#FB7185" }].map((h, i) => (
 <div key={i} className="absolute heart-float" style={{ left: h.l, bottom: "10%", animationDelay: `${h.d}ms` }}>
 <svg width="32" height="28" viewBox="0 0 32 28"><path d="M16 26 C 4 18, 0 10, 6 4 C 11 0, 14 4, 16 7 C 18 4, 21 0, 26 4 C 32 10, 28 18, 16 26 Z" fill={h.c} opacity="0.85" /></svg>
 </div>
 ))}
 {/* Sparkle stars */}
 {Array.from({ length: 7 }).map((_, i) => (
 <div key={i} className={`absolute twinkle twinkle-d${(i % 7) + 1} text-amber-200`} style={{ left: `${20 + i * 10}%`, top: `${25 + (i % 2) * 18}%`, fontSize: "1.4rem", textShadow: "0 0 14px hsl(35,100%,60%)" }}>★</div>
 ))}
 </div>
 );
}

const OVERLAY = { nye: NyeOverlay, corporate: CorporateOverlay, birthday: BirthdayOverlay, private: PrivateOverlay };

export default function HeroStory() {
 const [i, setI] = useState(0);
 const [tick, setTick] = useState(0); // re-trigger animations
 const slide = SLIDES[i];

 const go = useCallback((idx) => { setI(idx); setTick(t => t + 1); }, []);

 useEffect(() => {
 const t = setInterval(() => setI(prev => { setTick(tk => tk + 1); return (prev + 1) % SLIDES.length; }), DURATION);
 return () => clearInterval(t);
 }, []);

 const Overlay = OVERLAY[slide.id];

 return (
 <section className="relative min-h-screen flex items-end pb-12 lg:pb-20 overflow-hidden" data-testid="hero-story">
 {/* Crossfading photos */}
 {SLIDES.map((s, idx) => (
 <div key={s.id} className={`absolute inset-0 fade-slide ${i === idx ? "opacity-100" : "opacity-0"}`}>
 <img key={`${s.id}-${tick}`} src={s.photo} alt={s.tag} className={`w-full h-full object-cover ${i === idx ? "ken-burns" : ""}`} />
 </div>
 ))}
 {/* Base dark overlay (always) */}
 <div className="absolute inset-0 bg-[hsla(220,60%,8%,0.55)] pointer-events-none" />
 {/* Themed gradient wash flashing per slide */}
 <div key={`wash-${tick}`} className="absolute inset-0 flash-wash pointer-events-none" style={{ background: slide.wash }} />
 {/* Themed animated overlay (fireworks/champagne/balloons/bokeh) */}
 <div key={`ov-${tick}`} className="absolute inset-0">{Overlay && <Overlay />}</div>

 {/* Story progress bars at top */}
 <div className="absolute top-0 left-0 right-0 z-20 px-5 lg:px-10 pt-24 lg:pt-28">
 <div className="mx-auto max-w-7xl flex gap-1.5" data-testid="hero-progress">
 {SLIDES.map((_, idx) => (
 <div key={idx} className="flex-1 h-0.5 bg-white/25 rounded overflow-hidden">
 <div className={`h-full bg-white ${idx === i ? "story-fill" : ""}`} key={`fill-${idx}-${tick}`} style={{ width: idx < i ? "100%" : idx > i ? "0%" : undefined }} />
 </div>
 ))}
 </div>
 </div>

 {/* CONTENT */}
 <div className="relative mx-auto max-w-7xl px-5 lg:px-10 w-full grid lg:grid-cols-12 gap-10 items-end z-10">
 <div className="lg:col-span-8">
 <div key={`pill-${tick}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsla(220,60%,10%,0.55)] border border-white/15 backdrop-blur-sm ig-pop mb-4">
 <span className="text-yellow-300 text-sm">{slide.pillEmoji}</span>
 <span className="text-white text-[10px] uppercase tracking-[0.28em] font-bold">{slide.pillText}</span>
 </div>
 <div key={`eb-${tick}`} className={`${slide.eyebrowClass} text-[12px] uppercase tracking-[0.32em] font-bold ig-pop`}>{slide.eyebrow}</div>
 <h1 key={`h1-${tick}`} className="font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl text-white leading-[1.08] tracking-tight mt-3 max-w-3xl pr-2 pb-6 ig-pop drop-shadow-2xl" data-testid="hero-headline">
 {slide.title}
 </h1>
 <p key={`tag-${tick}`} className="text-2xl md:text-3xl font-light italic text-amber-200 mt-4 ig-pop">"{slide.tagline}"</p>
 <p key={`body-${tick}`} className="text-white/85 text-base lg:text-lg max-w-2xl mt-5 ig-pop leading-relaxed">{slide.body}</p>

 {/* Theme chips, clickable */}
 <div className="flex flex-wrap gap-2.5 mt-7">
 {SLIDES.map((s, idx) => (
 <button key={s.id} onClick={() => go(idx)} className={`chip-event ${s.chipClass} transition-all ${i === idx ? "scale-110 ring-2 ring-white/70" : "opacity-80 hover:opacity-100"}`} data-testid={`theme-chip-${s.id}`}>
 {s.tag}
 </button>
 ))}
 </div>

 <div className="flex flex-wrap gap-3 mt-7">
 <Link to="/contact" className="btn-yellow" data-testid="hero-enquire-btn">Get A Quote Now <ArrowRight className="w-4 h-4" /></Link>
 <Link to="/packages" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border-2 border-white/40 text-white px-8 py-4 rounded-full font-bold text-sm tracking-wide hover:bg-white hover:text-ink transition-all" data-testid="hero-packages-btn">View Cruise Packages</Link>
 </div>

 <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-10 max-w-2xl">
 <div className="flex items-center gap-3">
 <div className="flex">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-[hsl(48,100%,60%)] text-[hsl(48,100%,60%)]" />)}</div>
 <div><div className="font-display text-lg text-white leading-none">5.0 Rated</div><div className="text-[10px] uppercase tracking-wider text-white/65 mt-1 font-bold">500+ Happy Guests</div></div>
 </div>
 <div className="flex items-center gap-3"><Users className="w-5 h-5 text-[hsl(200,95%,75%)]" /><div><div className="font-display text-lg text-white leading-none">Up to 120 Guests</div><div className="text-[10px] uppercase tracking-wider text-white/65 mt-1 font-bold">Private Charter</div></div></div>
 <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[hsl(200,95%,75%)]" /><div><div className="font-display text-lg text-white leading-none">365 Days a Year</div><div className="text-[10px] uppercase tracking-wider text-white/65 mt-1 font-bold">Available 7 Days</div></div></div>
 </div>
 </div>

 {/* INSTAGRAM POLAROID, theme-synced, tilted */}
 <div className="lg:col-span-4 hidden lg:block">
 <div className="rotate-[4deg] hover:rotate-0 transition-transform duration-500" data-testid="hero-ig-card-tilt">
 <div key={`card-${tick}`} className="ig-pop bg-white rounded-2xl p-4 shadow-2xl" data-testid="hero-ig-card" style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)", border: "3px solid", borderImage: `${slide.wash} 1` }}>
 <div className="flex items-center gap-2 mb-3">
 <div className="w-10 h-10 rounded-full p-[2px]" style={{ background: slide.wash }}><div className="w-full h-full rounded-full bg-white flex items-center justify-center"><div className="w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(280,75%,60%)] to-[hsl(335,90%,60%)] flex items-center justify-center text-white text-xs font-bold">M</div></div></div>
 <div className="flex-1"><div className="font-bold text-sm text-ink">@magic.cruises</div><div className="text-[10px] text-ink-mute uppercase tracking-wider">Sydney Harbour · Live</div></div>
 <div className="text-ink-mute">···</div>
 </div>
 <div className="relative rounded-xl overflow-hidden aspect-square">
 <img src={slide.photo} alt={slide.tag} className="absolute inset-0 w-full h-full object-cover" />
 <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-[hsl(335,90%,60%)] text-white text-[9px] uppercase tracking-wider font-bold animate-pulse">● Live</div>
 <div className="absolute bottom-2 left-2"><span className={`chip-event ${slide.chipClass} text-[9px] py-1 px-2`}>{slide.tag}</span></div>
 </div>
 <div className="flex items-center gap-3 mt-3 text-ink text-lg">
 <span className="hover:scale-110 transition-transform cursor-pointer">♥</span>
 <span className="hover:scale-110 transition-transform cursor-pointer">🥂</span>
 <span className="hover:scale-110 transition-transform cursor-pointer">✦</span>
 <span className="ml-auto text-xs font-bold">2.4k likes</span>
 </div>
 <div className="text-xs text-ink mt-2"><strong>@magic.cruises</strong> <span className="text-ink-mute">{slide.tagline} {slide.hashtags}</span></div>
 {/* Dots */}
 <div className="flex items-center justify-center gap-1.5 mt-4" data-testid="hero-dots">
 {SLIDES.map((_, idx) => (
 <button key={idx} onClick={() => go(idx)} aria-label={`Slide ${idx + 1}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-ink" : "w-1.5 bg-ink/25 hover:bg-ink/50"}`} data-testid={`hero-dot-${idx}`} />
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>
 );
}
